import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Check, Lock, Loader2 } from 'lucide-react';

import { useAdmin, useAdminSession } from '@/app/admin-session';
import type { AdminPlan } from '@/contracts';
import { useTranslate, useTranslations } from '@/i18n/use-locale';
import { useLocalizedName } from '@/i18n/localized-name';
import { formatCount } from '@/lib/format';
import { useToast } from '@/components/ui/toast';
import { AdminTeamPanel } from './admin-team-panel';
import { QueryGate } from '@/components/ui/query-gate';
import { usePlansQuery, usePoliciesQuery } from '@/lib/queries';
import {
  useIsSavingPlans,
  useUpdateMfaMutation,
  useUpdatePlanMutation,
  useUpdatePolicyMutation,
} from '@/lib/mutations';
import { isApiError } from '@/lib/unwrap';
import { StatusBadge } from '@/components/ui/status-badge';
import { useEnumLabel } from '@/lib/labels';
import { Card } from '@/components/ui/card';
import { livePrice, FEATURE_KEYS, POLICY_KEYS } from './settings-catalogue';

export function SettingsRoute() {
  const t = useTranslations('admin.settings');
  const tAll = useTranslate();
  const localizedName = useLocalizedName();
  const featureLabel = useEnumLabel('admin.settings.planFeature');
  const { toast } = useToast();
  const admin = useAdmin();
  const { signIn } = useAdminSession();

  // The price editor holds only what the admin typed, keyed by plan id, over the live
  // plans. Nothing copies the query into state, so a refetch shows the server's answer
  // for every plan the admin has not touched, and an edit survives it.
  const [drafts, setDrafts] = useState<Record<string, number>>({});
  const [mfaPassword, setMfaPassword] = useState('');

  const plansQuery = usePlansQuery();
  const policiesQuery = usePoliciesQuery();
  const policyMutation = useUpdatePolicyMutation();
  const updatePlan = useUpdatePlanMutation();
  const savingPlans = useIsSavingPlans();

  const priceOf = (plan: AdminPlan) => drafts[plan.id] ?? livePrice(plan);
  const changed = (plansQuery.data ?? []).filter((plan) => priceOf(plan) !== livePrice(plan));
  const isDirty = changed.length > 0;

  const handlePriceChange = (planId: string, price: number) =>
    setDrafts((current) => ({ ...current, [planId]: price }));

  // One write per changed plan, side by side. A plan that saved drops its draft (the
  // cache already holds the answer); one that failed keeps it, so Save tries it again.
  const handleSavePlans = async () => {
    const results = await Promise.allSettled(
      changed.map((plan) => updatePlan.mutateAsync({ plan, priceMinor: priceOf(plan) * 100 })),
    );
    const saved = changed.filter((_, index) => results[index]?.status === 'fulfilled');
    setDrafts((current) =>
      Object.fromEntries(
        Object.entries(current).filter(([planId]) => !saved.some((plan) => plan.id === planId)),
      ),
    );
    if (saved.length === 0) toast({ title: t('plans.toastFailed'), tone: 'danger' });
    else toast({ title: t('plans.toastSaved', { count: formatCount(saved.length) }) });
  };

  const handleCancelPlans = () => setDrafts({});

  const mfaMutation = useUpdateMfaMutation({
    // The password was refused, or the request never got an answer.
    failToast: (error) => (isApiError(error) ? t('mfa.toastWrongPassword') : t('mfa.toastFailed')),
    onSuccess: (_identity, { enabled }) => {
      setMfaPassword('');
      // The session carries the flag the badge above reads; refresh it, then say so.
      signIn().then(
        () => toast({ title: enabled ? t('mfa.toastEnabled') : t('mfa.toastDisabled') }),
        () => toast({ title: t('mfa.toastFailed'), tone: 'danger' }),
      );
    },
  });

  const handleToggleMfa = (e: React.FormEvent) => {
    e.preventDefault();
    if (!mfaPassword.trim()) return;
    mfaMutation.mutate({ enabled: !admin.mfaEnabled, password: mfaPassword });
  };
  const mfaLoading = mfaMutation.isPending;

  // The plan's name in the admin's language. The Arabic rows carry a leading
  // subscription word the card title replaces with its own — display only, never saved.
  const planName = (plan: AdminPlan) => {
    const name = localizedName(plan);
    const prefix = `${t('plans.namePrefix')} `;
    return name.startsWith(prefix) ? name.slice(prefix.length).trim() : name;
  };

  return (
    <div className="grid gap-5 lg:grid-cols-2">
      {/* ═════════ START COLUMN: SUBSCRIPTION PLANS ═════════ */}
      <Card className="p-5">
        <h2 className="text-[15px] font-bold text-navy">{t('plans.title')}</h2>
        <p className="mb-4 text-[12.5px] text-conc-500">{t('plans.subtitle')}</p>

        <QueryGate query={plansQuery}>
          {(livePlans) => (
            <>
              <ul className="space-y-3">
                {livePlans.map((plan) => {
                  const name = planName(plan);
                  return (
                    <li key={plan.id} className="rounded-xl border border-conc-100 p-4">
                      <div className="flex items-center justify-between">
                        <p className="text-[14px] font-bold text-navy">
                          {t('plans.planName', { name })}
                        </p>
                        <div className="flex items-center gap-1">
                          <input
                            type="number"
                            value={priceOf(plan)}
                            aria-label={t('plans.priceLabel', { name })}
                            onChange={(e) => handlePriceChange(plan.id, Number(e.target.value))}
                            className="num w-24 rounded-lg border border-conc-100 bg-sideline px-2.5 py-1.5 text-end text-[13px] font-bold text-navy outline-none focus:border-pitch focus:bg-white"
                          />
                          <span className="text-[12.5px] font-semibold text-conc-500">
                            {tAll('common.currency.unit')}
                          </span>
                        </div>
                      </div>

                      <ul className="mt-3 space-y-1.5">
                        {plan.features.map((f) => (
                          <li
                            key={f}
                            className="flex items-center gap-2 text-[12.5px] text-conc-600"
                          >
                            <Check className="h-3.5 w-3.5 shrink-0 text-pitch" />
                            <span>{featureLabel(FEATURE_KEYS[f] ?? f)}</span>
                          </li>
                        ))}
                      </ul>
                    </li>
                  );
                })}
              </ul>

              <div className="mt-4 flex items-center gap-2">
                <Button
                  type="button"
                  onClick={() => void handleSavePlans()}
                  disabled={!isDirty || savingPlans}
                  className="flex-1"
                >
                  {savingPlans ? <Loader2 aria-hidden className="h-4 w-4 animate-spin" /> : null}
                  {savingPlans ? t('plans.saving') : t('plans.saveToDb')}
                </Button>
                {isDirty && (
                  <Button type="button" variant="outline" onClick={handleCancelPlans}>
                    {t('plans.cancel')}
                  </Button>
                )}
              </div>
            </>
          )}
        </QueryGate>
      </Card>

      {/* ═════════ END COLUMN: AUDIT POLICY, 2FA & ADMIN TEAM ═════════ */}
      <div className="space-y-5">
        {/* 1. AUDIT POLICY */}
        <Card className="p-5">
          <h2 className="text-[15px] font-bold text-navy">{t('policy.title')}</h2>
          <p className="mb-4 text-[12.5px] text-conc-500">{t('policy.subtitle')}</p>

          <QueryGate query={policiesQuery}>
            {(policies) => (
              <ul className="space-y-1">
                {policies.map((item) => {
                  const policyKey = POLICY_KEYS[item.key];
                  if (!policyKey) return null;
                  const label = t(`policy.${policyKey}.label`);
                  return (
                    <li key={item.key}>
                      <Button
                        type="button"
                        variant="ghost"
                        size="none"
                        role="switch"
                        aria-checked={item.enabled}
                        aria-label={label}
                        onClick={() =>
                          policyMutation.mutate({ key: item.key, enabled: !item.enabled })
                        }
                        disabled={policyMutation.isPending}
                        className="flex w-full items-start justify-start gap-3 rounded-xl p-3 text-start font-normal whitespace-normal disabled:opacity-60"
                      >
                        <span
                          aria-hidden
                          className={`mt-0.5 flex h-6 w-11 shrink-0 items-center rounded-full p-0.5 transition ${
                            item.enabled ? 'bg-pitch' : 'bg-conc-200'
                          }`}
                        >
                          <span
                            className={`h-5 w-5 rounded-full bg-white shadow transition-transform ${
                              item.enabled ? 'translate-x-5 rtl:-translate-x-5' : 'translate-x-0'
                            }`}
                          />
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="block text-[13.5px] font-semibold text-navy">
                            {label}
                          </span>
                          <span className="block text-[12px] leading-relaxed text-conc-500">
                            {t(`policy.${policyKey}.hint`)}
                          </span>
                        </span>
                      </Button>
                    </li>
                  );
                })}
              </ul>
            )}
          </QueryGate>
        </Card>

        {/* 2. 2FA SECURITY SECTION */}
        <Card className="p-5">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-[15px] font-bold text-navy">{t('mfa.title')}</h2>
              <p className="text-[12.5px] text-conc-500">{t('mfa.subtitle')}</p>
            </div>
            <StatusBadge tone={admin.mfaEnabled ? 'approved' : 'rejected'}>
              {admin.mfaEnabled ? t('mfa.enabled') : t('mfa.disabled')}
            </StatusBadge>
          </div>

          <form onSubmit={handleToggleMfa} className="mt-4 space-y-3 rounded-xl bg-sideline p-3.5">
            <p className="text-[12.5px] text-conc-600">
              {admin.mfaEnabled ? t('mfa.confirmDisable') : t('mfa.confirmEnable')}
            </p>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Lock className="absolute start-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-conc-400" />
                <input
                  type="password"
                  value={mfaPassword}
                  onChange={(e) => setMfaPassword(e.target.value)}
                  placeholder={t('mfa.passwordPlaceholder')}
                  className="w-full rounded-lg border border-conc-100 bg-white py-1.5 ps-8 pe-2.5 text-[12.5px] text-navy outline-none focus:border-pitch"
                  required
                />
              </div>
              <Button
                type="submit"
                variant={admin.mfaEnabled ? 'danger' : 'primary'}
                size="sm"
                disabled={!mfaPassword.trim() || mfaLoading}
                className="py-1.5"
              >
                {mfaLoading
                  ? t('mfa.working')
                  : admin.mfaEnabled
                    ? t('mfa.turnOff')
                    : t('mfa.turnOn')}
              </Button>
            </div>
          </form>
        </Card>

        {/* 3. ADMIN TEAM — real, from GET /admin/admins */}
        <AdminTeamPanel />
      </div>
    </div>
  );
}
